'use client';

import React, { FC, useState } from 'react';
import { useRouter } from '@/navigation';
import { TypeAddChildFormFields } from '@/core/generated-types';
import { Button, Errors, DynamicComponent } from '@/components';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { createChildRelationship } from '@/lib/api/accountManager/apis/createChildRelationship';
import useAuth from '@/context/AuthContext';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { ChildBirthDateInput } from '@/utils/dates/constants';
import { validateChildBirthDate } from '@/utils/dates/validateChildBirthDate';
import { childBirthDateToDateString } from '@/utils/dates/dateFormatters';
import { ProcessingButtonType } from '@/core/constants';
import { useSearchParams } from 'next/navigation';
import {
  buildRelativeInternalUrl,
  buildQueryString,
} from '@/utils/shopSubdomain';
import { TypeRelationshipRecord } from '@/lib/api/accountManager/types';
import { TypeResponseSuccess } from '@/lib/api/config';

const AddChildForm: FC<TypeAddChildFormFields> = ({
  confirmCta,
  cancelCta,
  nameInput,
  childBirthDateInput,
  title,
  subtitle,
  trailingText,
}) => {
  const [processingButton, setProcessingButton] =
    useState<ProcessingButtonType>('');
  const [childName, setChildName] = useState('');
  const [childBirthDate, setChildBirthDate] = useState<ChildBirthDateInput>({
    year: '',
    month: '',
    day: '',
  });
  const [childNameValidationError, setChildNameValidationError] = useState('');
  const [childBirthDateValidationError, setChildBirthDateValidationError] =
    useState('');
  const [serverError, setServerError] = useState(false);
  const searchParams = useSearchParams();

  const { back, push } = useRouter();
  const { user } = useAuth();
  const userUuid = user?.attributes['custom:account_manager_id'];
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(errorDirectives);

  const clearServerError = () => {
    if (serverError) {
      setServerError(false);
    }
  };

  function returnToSourcePage(createdChildUuid: string) {
    const pageSource = searchParams?.get('page_source');
    let url = buildRelativeInternalUrl(['account', 'settings']);

    if (pageSource === 'subscriber_shop_onboarding') {
      url = buildRelativeInternalUrl(['account', 'subscriber-shop', 'family']);
    }

    if (pageSource === 'subscriptions') {
      const queryString = buildQueryString([
        {
          key: 'updated_child_uuid',
          value: createdChildUuid,
        },
      ]);
      url = buildRelativeInternalUrl(['account', 'subscriptions'], queryString);
    }

    push(url);
  }

  const updateChildName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChildName(event.target.value);
    if (childNameValidationError) {
      setChildNameValidationError('');
    }

    clearServerError();
  };

  const updateChildBirthDate = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    setChildBirthDate((prevState) => ({
      ...prevState,
      [type]: event.target.value,
    }));

    clearServerError();
  };

  const validateChildName = (target: any) => {
    if (nameInput.fields.required && !childName) {
      return nameInput.fields.requiredError || '';
    }

    if (target && !target.checkValidity()) {
      return nameInput.fields.validationError || '';
    }
    return '';
  };

  const submitAddChildForm = async (event: any) => {
    setProcessingButton('confirm');
    event.preventDefault();

    const freeformDateToDateString = childBirthDateToDateString(childBirthDate);
    const childBirthDateAsDate = new Date(freeformDateToDateString);
    const childBirthDateError = validateChildBirthDate(
      event,
      childBirthDateAsDate,
      childBirthDate,
      childBirthDateInput,
    );
    const childNameError = validateChildName(
      nameInput.fields.id && event.target[nameInput.fields.id],
    );

    setChildNameValidationError(childNameError);
    setChildBirthDateValidationError(childBirthDateError);

    if (childNameError || childBirthDateError) {
      setProcessingButton('');
      return;
    }

    const serverSupportedDateString = childBirthDateAsDate
      .toISOString()
      .split('T')[0];
    const res = await createChildRelationship({
      childName,
      childBirthDate: serverSupportedDateString,
      userUuid,
    });

    if (res.ok) {
      const responseSuccess =
        res as TypeResponseSuccess<TypeRelationshipRecord>;
      const createdChildUuid = responseSuccess.data.child.uuid;
      returnToSourcePage(createdChildUuid);
    } else {
      setServerError(true);
      setProcessingButton('');
    }
  };

  return (
    <div
      data-testid="add-a-child-form"
      className="flex max-w-[500px] flex-col justify-center space-y-4"
    >
      {serverError && <Errors errors={errors} heading={heading} />}
      {title && (
        <Heading
          tag={HeadingTag.H1}
          size={HeadingSize.H1}
          text={title}
          align="left"
        />
      )}
      <p>{subtitle}</p>
      <form onSubmit={submitAddChildForm} noValidate>
        <div className="space-y-3 pb-7">
          <DynamicComponent
            component={nameInput}
            value={childName}
            error={childNameValidationError}
            onChange={updateChildName}
          />
        </div>
        <div className="space-y-3 pb-7">
          <DynamicComponent
            component={childBirthDateInput}
            value={childBirthDate}
            error={childBirthDateValidationError}
            onChange={updateChildBirthDate}
          />
        </div>
        <div className="w-full space-y-4 pb-5 md:w-auto">
          <Button
            type="submit"
            ctaText={confirmCta}
            disabled={processingButton !== ''}
            includeSpinner={processingButton === 'confirm'}
            fullWidth
          />
          <Button
            ctaText={cancelCta}
            style={BUTTON_STYLES.secondary}
            onClick={() => {
              setProcessingButton('cancel');
              back();
            }}
            disabled={processingButton !== ''}
            includeSpinner={processingButton === 'cancel'}
            fullWidth
          />
        </div>
      </form>
      {trailingText && (
        <p className="!mt-0 text-center text-base">{trailingText}</p>
      )}
    </div>
  );
};

export default React.memo(AddChildForm);
